<!--
  计量分析 - 共现/作者/机构/聚类网络图
  图谱：公共组件 D3KnowledgeGraph（力导向 + Canvas）；导出：html2canvas
-->
<template>
    <div class="analysis-complex">
        <!-- 下载当前图为 PNG -->
        <div @click="save_pic" class="down-img">
            <div class="down"></div>
        </div>
        <d3-knowledge-graph
            ref="d3Graph"
            class="container complex-d3DIV"
            :graph-data="newData"
            :side-label="sideLabel"
            :preserve-node-style="true"
            :cluster-key-fn="clusterKey"
            :variable-radius="true"
            :link-distance="100"
            :layout-ticks="500"
            :layout-charge="-180"
            :layout-cluster-strength="0.36"
            :ring-radius-ratio="0.28"
            :initial-jitter="50"
            :node-label-max-len="12"
            :zoom-scale-extent="zoomScaleExtent"
        />
    </div>
</template>

<script>
import modelTip from "../../../components/ui-kit/model_tip.vue"
import D3KnowledgeGraph from "../../../components/D3KnowledgeGraph.vue"
import html2canvas from 'html2canvas'

export default {
    /** graphList: { nodes, edges }；containerId: 容器 DOM id；complexType: author | group | keywordPair | curveLPair */
    props: ['graphList', 'containerId', 'complexType'],
    components: {
        modelTip,
        D3KnowledgeGraph,
    },
    data () {
        return {
            graph: null,
            /** 与 D3KnowledgeGraph 的 graphData 一致 */
            newData: { nodes: [], edges: [] },
            /** 与原先 graph_tab 中 d3.zoom 范围一致 */
            zoomScaleExtent: [0.12, 5],
            nodeFrom: {
                minWeight: '10',
                limitEdgeCount: '100',
                layCount: 3,
                sorce: 1.3,
                nodeNames: '',
            },
            isNodata: false,
            /** 边标签默认策略：hidden 时仅悬停显示；show 时半透明显示 */
            sideLabel: 'hidden',
        }
    },
    watch: {

    },
    mounted () {
        console.log(this.graphList,this.complexType)
        this.getData()
    },
    beforeDestroy () {
    },
    methods: {
        /** 重置无数据标记 */
        initData () {
            this.isNodata = false
        },
        /** 入口：拉取并渲染（数据来自 props.graphList） */
        getData () {
            this.initData()
            this.data_process()
        },
        /** 组装 newData、样式；图谱由 D3KnowledgeGraph 监听 graphData 渲染 */
        data_process () {
            const nodesData = this.graphList && this.graphList.nodes || []
            const edgesData = this.graphList && this.graphList.edges || []
            if (nodesData.length === 0) {
                this.isNodata = true
                this.newData = { nodes: [], edges: [] }
                return
            }
            this.newData = {
                nodes: [],
                edges: [],
            }
            nodesData && nodesData.forEach((item, index) => {
                this.$set(item, 'num', index + 1)
                this.newData.nodes.push(item)
            })
            edgesData && edgesData.forEach((item, index) => {
                this.$set(item, 'num', index + 1)
                this.newData.edges.push(item)
            })
            this.set_nodeStyle()
            this.$nextTick(() => {
                setTimeout(() => this.report_pic(), 3000)
            })
        },
        /**
         * 按 complexType 设置节点大小与默认颜色
         * curveLPair：用 node.distance 填色（聚类色带）
         * group/author、keywordPair：按 degree 规则设 size，并写入 _r 作为 Canvas 半径
         */
        set_nodeStyle () {
            const _this = this
            this.newData && this.newData.nodes.forEach(function (node) {
                if (!node.style) {
                    node.style = {
                        fill: '#3ba7fa',
                        stroke: '#41e976',
                        lineWidth: 2,
                    }
                }
                if (_this.complexType === 'curveLPair') {
                    _this.node_fill(node)
                }
                if (_this.complexType === 'group' || _this.complexType === 'author') {
                    if (node.degree < 3) {
                        node.size = 20
                    } else if (node.degree > 3 && node.degree < 10) {
                        node.size = node.degree * 10
                    } else if (node.degree > 10) {
                        node.size = 120
                    }
                } else if (_this.complexType === 'keywordPair' || _this.complexType === 'curveLPair') {
                    if (node.degree <= 10) {
                        node.size = 10
                    } else if (node.degree > 10 && node.degree < 120) {
                        node.size = node.degree
                    } else if (node.degree >= 120) {
                        node.size = 120
                    }
                }
                if (node.size == null) node.size = 10
                node._r = Math.max(4, (node.size || 10) / 2)
            })
        },
        /** 关键词聚类：填充/描边使用接口给出的 distance（颜色字符串） */
        node_fill (node) {
            node.style.fill = node.distance
            node.style.stroke = node.distance
        },
        /**
         * 集群划分键：优先 cluster / clusterId / community；
         * 否则按数值 degree 分档；无 degree 归为「其他」
         */
        clusterKey (d) {
            const c = d.cluster != null ? d.cluster : (d.clusterId != null ? d.clusterId : d.community)
            if (c != null && c !== '') return String(c)
            const v = d.degree
            if (v == null || v === '') return '其他'
            if (typeof v === 'string' && isNaN(Number(v))) return String(v)
            const n = Number(v)
            if (isNaN(n)) return String(v)
            if (n < 5) return '≤4'
            if (n < 15) return '5–14'
            if (n < 40) return '15–39'
            return '≥40'
        },
        /** 容器 DOM 截图转 PNG（提高 devicePixelRatio 略增清晰度） */
        exportCanvasPng () {
            const el = this.$refs.d3Graph && this.$refs.d3Graph.$el
            if (!el) return Promise.reject(new Error('no container'))
            window.oldRatio = window.devicePixelRatio
            window.devicePixelRatio = 4
            return html2canvas(el, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false,
            }).then((canvas) => {
                window.devicePixelRatio = window.oldRatio
                return canvas.toDataURL('image/png')
            }).catch((err) => {
                window.devicePixelRatio = window.oldRatio
                throw err
            })
        },
        /** 用户点击下载「网络分析图.png」 */
        save_pic () {
            this.exportCanvasPng().then((dataUrl) => {
                const a = document.createElement('a')
                a.download = '网络分析图.png'
                a.href = dataUrl
                a.click()
            }).catch(() => {
                this.$message && this.$message.error('导出图片失败')
            })
        },
        /** 供 atlas_result 生成报告：传出 complexType 与 base64 图 */
        report_pic () {
            this.exportCanvasPng().then((reportImg) => {
                this.$emit('comples_img', this.complexType, reportImg)
            }).catch(() => {
                this.$emit('comples_img', this.complexType, '')
            })
        },
    },
}
</script> 
<style lang="less" scoped>
    /* 图谱区域与下载按钮布局 */
    .text-tip{
        .icon{
            display: inline-block;
            vertical-align: middle;
            width:16px;
            height: 16px;
            border-radius: 50%;
            background: #fdcb02;
        }
    } 
.collapse-info{
    ::v-deep .el-form-item{
        padding-right: 20px;
        .el-slider__stop{
            background: #79bbff;
        }
        
    }
    
   
}
.analysis-complex{
    width: 1200px;
    margin: 0 auto;
    border: 1px dashed #79bbff;
    margin-bottom: 20px;
    .container{
        width: 100%;
        height: 700px;
        margin-bottom: 20px;
    }
    .down-img{
        display: flex;
        justify-content: flex-end;
        margin-bottom: 10px;
        .down{
            width: 16px;
            height: 16px;
            background: url('../../../assets/images/bibliometric/down-img.png') no-repeat;
            background-size: cover;
            cursor: pointer;
        }
        
    }
}
</style>
